// =====================================================================
// FIRMAR CON LA E.FIRMA EN EL NAVEGADOR · ESTABILIDAD F6 · E2.1
// =====================================================================
// La e.firma es la firma electrónica del SAT: el archivo .cer (público),
// el archivo .key (la llave privada, cifrada) y su contraseña.
//
// LA LLAVE PRIVADA NUNCA SALE DE LA COMPUTADORA. Aquí se abre el .key con
// la contraseña, se firma el reto que dio TEGO y se descarta. Al servidor
// viajan SOLO el .cer y la firma. La contraseña tampoco viaja.
//
// POR QUÉ UNA BIBLIOTECA. El .key del SAT es PKCS#8 cifrado con PBES2 y
// triple DES (des-ede3-cbc); WebCrypto no descifra triple DES. Se usa
// node-forge 1.4.0 (licencia BSD-3-Clause, doble con GPL-2.0), VENDIDA en
// `public/vendor/node-forge/` —no desde un CDN: la política de contenido
// del sitio solo carga guiones del propio origen— y se pide SOLO en esta
// pantalla, cuando hace falta.
//
// La firma: RSA PKCS#1 v1.5 con SHA-256 sobre el texto del reto en UTF-8,
// que es lo que el servidor comprueba (`backend/app/seguridad/efirma.py`).

export const RUTA_FORGE = '/vendor/node-forge/forge.min.js';

let cargando = null;

/** Carga node-forge una sola vez, desde el propio sitio. */
export function cargarForge() {
  if (typeof window !== 'undefined' && window.forge) return Promise.resolve(window.forge);
  if (cargando) return cargando;
  cargando = new Promise((resolver, rechazar) => {
    const s = document.createElement('script');
    s.src = RUTA_FORGE;
    s.async = true;
    s.onload = () => (window.forge ? resolver(window.forge)
      : rechazar(new Error('No se cargó la herramienta de firma. Recarga la página y vuelve a intentarlo.')));
    s.onerror = () => {
      cargando = null;
      rechazar(new Error('No se cargó la herramienta de firma: revisa tu conexión y vuelve a intentarlo.'));
    };
    document.head.appendChild(s);
  });
  return cargando;
}

/** Uint8Array → cadena binaria (lo que forge entiende). */
function aBinario(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return s;
}

/** Uint8Array → base64. */
export function aBase64(bytes) {
  return btoa(aBinario(bytes));
}

/** Lee un archivo elegido en el navegador como bytes. */
export async function leerArchivo(archivo) {
  return new Uint8Array(await archivo.arrayBuffer());
}

export class ErrorDeFirma extends Error {
  constructor(mensaje, campo) {
    super(mensaje);
    this.campo = campo;
  }
}

/**
 * Abre el .key con la contraseña y firma el reto. Devuelve
 * `{ certificado, firma }` en base64, listos para mandar.
 * Lanza ErrorDeFirma con el campo que hay que revisar.
 */
export function firmarReto(forge, { cer, key, contrasena, reto }) {
  if (!cer?.length) throw new ErrorDeFirma('Falta el archivo .cer de tu e.firma.', 'cer');
  if (!key?.length) throw new ErrorDeFirma('Falta el archivo .key de tu e.firma.', 'key');
  if (!contrasena) throw new ErrorDeFirma('Falta la contraseña de tu e.firma.', 'contrasena');

  let privada;
  try {
    const cifrada = forge.asn1.fromDer(aBinario(key));
    const info = forge.pki.decryptPrivateKeyInfo(cifrada, contrasena);
    if (!info) throw new Error('contraseña');
    privada = forge.pki.privateKeyFromAsn1(info);
  } catch {
    throw new ErrorDeFirma(
      'La contraseña no abre ese archivo .key. Escribe la contraseña de tu e.firma (la de la '
      + 'llave privada, no la del portal del SAT) y revisa que elegiste el archivo .key.',
      'contrasena');
  }

  const md = forge.md.sha256.create();
  md.update(reto, 'utf8');
  const firma = privada.sign(md);

  // Comprobación local: que el .key y el .cer sean de la misma e.firma.
  // Si el certificado no se puede leer aquí, lo decide el servidor.
  let publica = null;
  try {
    publica = forge.pki.certificateFromAsn1(forge.asn1.fromDer(aBinario(cer))).publicKey;
  } catch {
    publica = null;
  }
  if (publica) {
    let cuadra = false;
    try {
      const md2 = forge.md.sha256.create();
      md2.update(reto, 'utf8');
      // Con llaves de distinto tamaño forge lanza en vez de contestar «no».
      cuadra = publica.verify(md2.digest().bytes(), firma);
    } catch {
      cuadra = false;
    }
    if (!cuadra) {
      throw new ErrorDeFirma(
        'El archivo .key no es de ese .cer: elige los dos archivos de la MISMA e.firma.', 'cer');
    }
  }

  return { certificado: aBase64(cer), firma: btoa(firma) };
}
